import moment from "moment";
import { formatRelativeTime, momentLocaleFor } from "../Helper/RelativeTime";
import { create } from "zustand";
import { resolveHomeVersion } from "../Helper/homeVersion";
import { useSessionStore } from "./Session";
import { useTicketStore } from "./Ticket";

export interface AuroraConfig {
  colors: string[]; // 5 hex strings, warm→cool
  source: "auto" | "custom";
  seed: string;
}
// MUST match Frontend/src/helper/auroraDefaults.ts DEFAULT_AURORA.
export const DEFAULT_AURORA: string[] = [
  "#A894FF",
  "#FFA8D6",
  "#9E8CFF",
  "#82C2FF",
  "#8CE8D6",
];

export type ConfigMenuItem = {
  title?: string;
  infoDescription?: string;
  description?: string;
  botId?: string;
  icon?: string;
  actionType?: string;
  actionBody?: string;
  color?: string;
  actionOpenInNewTab?: boolean;
  targetAudience?: string;
  closeWidgetAfterAction?: boolean;
  /** "Pass to agent": names the team's extra instructions for Kai (the server keeps their text). */
  kaiInstructionsKey?: string;
};

export type Config = {
  showOnlineStatus?: boolean;
  replyTime?: string;
  showSources?: boolean;
  alwaysOnline?: boolean;
  stn?: boolean;
  sts?: boolean;
  stt?: boolean;
  sendOnEnter?: boolean;
  showNoFeatureSuggestions?: boolean;
  bgType?: string;
  showNewsOnHome?: boolean;
  showHelpSearchOnHome?: boolean;
  bgBlur?: boolean;
  bgImage?: string;
  helpSearch?: string;
  // The configured home version. Absent on every project that never picked one
  // in the dashboard — read it through getHomeVersion(), never raw.
  v?: number;
  openingTimes?: any[];
  // Upcoming closed days (public holidays + custom days) as `YYYY-MM-DD`.
  closedDates?: string[];
  animateBG?: boolean;
  fadebg?: boolean;
  helpcenterConfig?: any;
  timezone?: string;
  helpSearchLabel?: string;
  newsMenuLabel?: string;
  checklistMenuLabel?: string;
  knowledgeBaseMenuLabel?: string;
  featureRequestsMenuLabel?: string;
  allConversationsMenuLabel?: string;
  homeLabel?: string;
  name?: string;
  color?: string;
  localizationOptions?: {
    rtl?: boolean;
    language?: string;
  };
  showLatestConversation?: boolean;
  customCSS?: string;
  thankYouDuration?: number;
  backgroundColor?: string;
  headerColor?: string;
  // Second/third header colours for the classic/gradient header styles; the
  // v4 colour header renders a headerColor → headerColor2 gradient. Read via
  // getHeaderColorSecondary, which falls back to headerColor when unset.
  headerColor2?: string;
  headerColor3?: string;
  buttonColor?: string;
  borderRadius?: number;
  enabFeatReqBut?: string;
  logo?: string;
  disableBGFade?: boolean;
  disableBGGradient?: boolean;
  hideLogo?: boolean;
  buttonLogo?: string;
  hideBranding?: boolean;
  showHelpCenter?: boolean;
  hideAllConversations?: boolean;
  showMsgBtnOnConvTab?: boolean;
  showConvFilters?: boolean;
  hideKnowledgeBase?: boolean;
  knowledgeBaseExternalUrl?: string;
  hideNews?: boolean;
  disableAttachments?: boolean;
  showHomeHelpCenter?: boolean;
  hideChecklists?: boolean;
  showNoHomeNews?: boolean;
  showNoHomeChecklist?: boolean;
  hideFeatureRequests?: boolean;
  enableConsoleLogs?: boolean;
  spamProtection?: boolean;
  enableReplays?: boolean;
  replaysInterval?: number;
  enableNetworkLogs?: boolean;
  networkLogPropsToIgnore?: string[];
  enablePrivacyPolicy?: boolean;
  enableShortcuts?: boolean;
  privacyPolicyURL?: string;
  enableMenu?: boolean;
  activationMethodFeedbackButton?: boolean;
  activationMethodShake?: boolean;
  activationMethodScreenshotGesture?: boolean;
  enableCrashDetector?: boolean;
  crashDetectorIsSilent?: boolean;
  enableRageClickDetector?: boolean;
  rageClickDetectorIsSilent?: boolean;
  enableNetworkLogFilters?: boolean;
  jsMajorVersion?: string;
  enableIntercomCompatibilityMode?: boolean;
  hideWavingHandAfterName?: boolean;
  vision?: boolean;
  hideUsersName?: boolean;
  hideTeam?: boolean;
  // Who fronts a conversation's header once the AI is no longer handling it:
  // "ai-team" (default) = assignee / last replying agent / stacked team
  // avatars; "ai-only" = always the AI agent; "ai-assignee" = the assigned
  // team member, the AI agent while nobody is assigned.
  ticketHeaderMode?: "ai-team" | "ai-only" | "ai-assignee";
  widgetButtonText?: string;
  welcomeText?: string;
  showInfoPopup?: boolean;
  feedbackButtonPosition?: string;
  staticTranslations?: any;
  operatorAvatarImage?: string;
  operatorName?: string;
  // Derived server-side per request: a live workflow picks new conversations
  // up, so a new conversation's header shows the bot while it is created.
  conversationStartsWithBot?: boolean;
  // Kai Voice ("Speak to Kai") — derived server-side in prepareFlowConfig;
  // widget only ever sees {enabled, maxDurationSec, protocol}.
  kaiVoice?: {
    enabled?: boolean;
    maxDurationSec?: number;
  };
  audio?: boolean;
  audioMaxLength?: number;
  showSpecialAnnouncement?: boolean;
  specialAnnouncement?: string;
  enableUserComm?: string;
  enablePublicComments?: string;
  enabVoting?: string;
  defaultDomainHelpCenter?: string;
  // Regional base of the default help center host ("gleap.help" /
  // "us.gleap.help"). Absent on servers that predate it → "gleap.help".
  helpCenterBaseDomain?: string;
  customDomainHelpCenter?: string;
  startPage?: string;
  trackWidgetInteractions?: boolean;
  chatbarPlaceholder?: string;
  // The server sends localized quick actions as plain strings (max 2). The SDK's
  // setQuickActions() may pass objects, so accept both shapes — see normalizeQuickAction
  // in ChatbarView.
  chatbarQuickActions?: (string | { label?: string; text?: string })[];
  // One of: "light-glow" (default), "light", "dark-glow", "dark".
  chatbarStyle?: "light-glow" | "light" | "dark-glow" | "dark" | string;
  chatbarColor?: string;
  // Workflow to run when the chatbar is used. null/empty = default "Start
  // conversation" (server runs the on-conversation-started lookup).
  chatbarWorkflowId?: string | null;
  // v4 home: show the idle animated aurora glow inside the hero composer.
  homeComposerGlow?: boolean;
  // Optional v4 menu presentation. Existing widgets keep their pill layout.
  homeMenuStyle?: "pills" | "list";
  homeMenuTitle?: string;
  aurora?: AuroraConfig;
  // Organisation setting "Enforce EU AI Act" (default off): every AI surface
  // carries a visible, non-removable AI label.
  enforceEuAiAct?: boolean;
};

interface ConfigState {
  config: Config;
  team: {
    firstName: string;
    profileImageUrl: string;
    id: string;
  }[];
  homeWidgetsLoaded: boolean;
  homeWidgets: {
    type: "menu" | "app";
    config: any;
    targetAudience: "all" | "guests" | "users";
    conditions: any[];
  }[];
  agentTools: any[];
  isApp: boolean;
  /** The native host fires its survey callbacks from outbound-sent (config-update surveyCallbacks). */
  hostSurveyCallbacks: boolean;
  overrideLanguage: string;
  deniedAppPermissions: any[];
  appPermissionsLoaded: boolean;
  loadAction: (actionId: string) => any;
  setOverrideLanguage: (overrideLanguage: string) => void;
  setConfig: (config: any) => void;
  getUIColor: () => string;
  isRTLLang: () => boolean;
  translateText: (key?: string) => string;
  translateTextWithFallback: (
    key: string | undefined,
    fallback: string,
  ) => string;
  translateTextWithVars: (key?: string, vars?: any) => string;
  /**
   * Like translateTextWithVars, but returns `fallback` when no translation is
   * loaded for the key (translateText echoes the key itself in that case).
   */
  translateTextWithVarsAndFallback: (
    key: string | undefined,
    vars: any,
    fallback: string,
  ) => string;
  getBotName: () => string;
  isEuAiActEnforced: () => boolean;
  getHeaderColor: () => string;
  getHeaderColorSecondary: (key: string) => string;
  getBackgroundColor: () => string;
  loadTeamInfo: () => void;
  loadHomeWidgets: () => void;
  loadAppPermissions: () => void;
  updateMomentLanguage: () => void;
  /** The language widget dates and relative times are formatted in. */
  getTimeLanguage: () => string;
  /** "in 4 days" in the widget language (Intl fallback where moment has none). */
  formatRelativeTime: (target: moment.MomentInput) => string;
  getCurrentLanguage: (short?: boolean) => string;
  getButtonColor: () => string;
  getBorderRadius: (multiplier?: number) => number;
  getAuroraColors: () => string[] | undefined;
  getHomeVersion: () => number;
  setAgentTools: (agentTools: any[]) => void;
}

export const useConfigStore = create<ConfigState>()((set, get) => ({
  config: {},
  team: [],
  homeWidgets: [],
  homeWidgetsLoaded: false,
  deniedAppPermissions: [],
  appPermissionsLoaded: false,
  agentTools: [],
  isApp: false,
  hostSurveyCallbacks: false,
  overrideLanguage: "",
  setAgentTools: (agentTools) => {
    set({
      agentTools,
    });
  },
  loadHomeWidgets() {
    const sessionStore = useSessionStore.getState();
    // Stale-while-revalidate: keep the rendered home widgets while the
    // refresh runs and swap them when the response lands — clearing first
    // made every home item vanish and reappear on each widget open. Only the
    // very first load (nothing cached yet) shows the loading state.
    if (get().homeWidgets.length === 0) {
      set({ homeWidgetsLoaded: false });
    }

    var xhr = new XMLHttpRequest();
    xhr.open(
      "GET",
      sessionStore.apiUrl + "/config/" + sessionStore.sdkKey + "/homewidgets",
    );
    if (sessionStore.session) {
      xhr.setRequestHeader("Api-Token", sessionStore.sdkKey);
      xhr.setRequestHeader("Gleap-Id", sessionStore.session.gleapId ?? "");
      xhr.setRequestHeader("Gleap-Hash", sessionStore.session.gleapHash ?? "");
    }
    xhr.onreadystatechange = function () {
      if (xhr.readyState == 4) {
        if (xhr.status === 200 && xhr.responseText) {
          try {
            const data = JSON.parse(xhr.responseText);
            if (data && Array.isArray(data)) {
              // Replace wholesale — an empty array is a real answer (all
              // widgets removed) and must clear the stale ones.
              set({ homeWidgets: data, homeWidgetsLoaded: true });
              return;
            }
          } catch (exp) {}
        }
        // Failed refresh: keep whatever was rendered.
        set({ homeWidgetsLoaded: true });
      }
    };

    xhr.send();
  },
  loadAppPermissions() {
    const sessionStore = useSessionStore.getState();
    set({
      deniedAppPermissions: [],
      appPermissionsLoaded: true,
    });

    var xhr = new XMLHttpRequest();
    xhr.open(
      "GET",
      sessionStore.apiUrl +
        "/config/" +
        sessionStore.sdkKey +
        "/apppermissions",
    );
    if (sessionStore.session) {
      xhr.setRequestHeader("Api-Token", sessionStore.sdkKey);
      xhr.setRequestHeader("Gleap-Id", sessionStore.session.gleapId ?? "");
      xhr.setRequestHeader("Gleap-Hash", sessionStore.session.gleapHash ?? "");
    }
    xhr.onload = function () {
      if (xhr.status == 200) {
        set({
          appPermissionsLoaded: true,
        });
      }
    };
    xhr.onerror = function () {
      set({
        appPermissionsLoaded: true,
      });
    };
    xhr.onreadystatechange = function () {
      if (xhr.readyState == 4 && xhr.status == 200 && xhr.responseText) {
        try {
          const data = JSON.parse(xhr.responseText);
          if (data && data.length > 0) {
            set({
              deniedAppPermissions: data,
              appPermissionsLoaded: true,
            });
          }
        } catch (exp) {}
      }
    };

    xhr.send();
  },
  loadTeamInfo() {
    const sessionStore = useSessionStore.getState();
    // Keep the rendered team avatars while refreshing (no clear-then-fetch
    // flicker on widget open); the response replaces them.

    var xhr = new XMLHttpRequest();
    xhr.open(
      "GET",
      sessionStore.apiUrl + "/config/" + sessionStore.sdkKey + "/team",
    );
    xhr.onreadystatechange = function () {
      if (xhr.readyState == 4 && xhr.status == 200 && xhr.responseText) {
        try {
          const data = JSON.parse(xhr.responseText);
          if (data && data.length > 0) {
            set({
              team: data,
            });
          }
        } catch (exp) {}
      }
    };

    xhr.send();
  },
  loadAction(actionId) {
    return new Promise((resolve, reject) => {
      const sessionStore = useSessionStore.getState();
      const currentLanguage = useConfigStore.getState().getCurrentLanguage();
      const url = `${sessionStore.apiUrl}/config/${sessionStore.sdkKey}/action/${actionId}?lang=${currentLanguage}`;

      var xhr = new XMLHttpRequest();
      xhr.open("GET", url);

      xhr.onload = function () {
        if (xhr.status == 200) {
          try {
            const data = JSON.parse(xhr.responseText);
            resolve(data);
          } catch (exp) {
            reject(exp);
          }
        } else {
          reject(new Error());
        }
      };

      xhr.onerror = function () {
        reject(new Error());
      };

      xhr.send();
    });
  },
  getBotName: () => {
    const config = get().config;
    if (config?.operatorName && config?.operatorName.length > 0) {
      return config?.operatorName;
    }
    return "Kai";
  },
  isEuAiActEnforced: () => get().config?.enforceEuAiAct === true,
  translateText: (key) => {
    if (!key) {
      return "";
    }

    const {
      config: { staticTranslations },
    } = get();

    if (typeof staticTranslations === "undefined") {
      return key;
    }

    if (staticTranslations && staticTranslations[key]) {
      return staticTranslations[key];
    }

    return key;
  },
  translateTextWithFallback: (key, fallback) => {
    if (!key) {
      return fallback;
    }

    const {
      config: { staticTranslations },
    } = get();

    if (staticTranslations && staticTranslations[key]) {
      return staticTranslations[key];
    }

    return fallback;
  },
  translateTextWithVars: (key, vars) => {
    if (!key) {
      return "";
    }

    var template = get().translateText(key);
    if (!template) {
      return "";
    }

    return template.replace(/{(\w+)}/g, function (_, key) {
      return vars[key];
    });
  },
  translateTextWithVarsAndFallback: (key, vars, fallback) => {
    if (!key) {
      return fallback;
    }
    const template = get().translateText(key);
    if (!template || template === key) {
      return fallback;
    }
    const text = get().translateTextWithVars(key, vars).trim();
    return text.length > 0 ? text : fallback;
  },
  isRTLLang: () => {
    const {
      config: { localizationOptions },
    } = get();

    return localizationOptions?.rtl || false;
  },
  getCurrentLanguage: (short = false) => {
    const config = get().config;

    // Use normalized language from server if available
    if (config?.localizationOptions?.language) {
      const normalizedLang = config.localizationOptions.language;
      if (short) {
        return normalizedLang.substring(0, 2);
      }
      return normalizedLang;
    }

    // Fallback to browser/override language
    const overrideLanguage = get().overrideLanguage;
    var language = "en";
    if (typeof navigator.language !== "undefined") {
      language = navigator.language.toLowerCase();
    }
    if (overrideLanguage && overrideLanguage !== "") {
      language = overrideLanguage.toLowerCase();
    }
    if (short) {
      return language.substring(0, 2);
    }
    return language;
  },
  setConfig: (config: any) => {
    set({
      config,
    });
    get().updateMomentLanguage();

    if (config.showConvFilters) {
      useTicketStore.getState().setTicketFilter({ status: "OPEN" });
    } else {
      useTicketStore.getState().setTicketFilter({ status: undefined });
    }
  },
  setOverrideLanguage: (overrideLanguage: string) => {
    set({ overrideLanguage });
    get().updateMomentLanguage();
    try {
      document.documentElement.lang = overrideLanguage;
    } catch {}
  },
  getTimeLanguage: () => {
    const config = get().config;
    return (
      config?.localizationOptions?.language || get().getCurrentLanguage(true)
    );
  },
  updateMomentLanguage: () => {
    // Explicit mapping: moment ignores unknown keys (zh, zh_TW, no) and would
    // otherwise keep formatting in English.
    moment.locale(momentLocaleFor(get().getTimeLanguage()) ?? "en");
  },
  formatRelativeTime: (target) =>
    formatRelativeTime(target, get().getTimeLanguage()),
  getUIColor: () => {
    return get().config.color || "#000000";
  },
  getHeaderColor: () => {
    return get().config.headerColor || "#000000";
  },
  getHeaderColorSecondary: (key: string) => {
    return (get().config as any)[key] || get().getHeaderColor();
  },
  getBackgroundColor: () => {
    return get().config.backgroundColor || "#ffffff";
  },
  getButtonColor: () => {
    return get().config.buttonColor || "#000000";
  },
  getBorderRadius: (multiplier = 1) => {
    const staticFactor = 0.8;
    return Math.round(26 * multiplier * staticFactor);
  },
  getAuroraColors: () => {
    const colors = get().config?.aurora?.colors;
    return Array.isArray(colors) && colors.length === 5 ? colors : undefined;
  },
  getHomeVersion: () => resolveHomeVersion(get().config?.v),
}));
